import { LOCALE_NAMES, parseMultiselectValue } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { fieldAppliesToClient } from "@repo/shared/types";
import { useEffect, useState, useTransition } from "react";
import { CountrySelect } from "@/components/country-select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogBody,
  DialogCancel,
  DialogDescription,
  DialogScrollContent,
  DialogScrollFooter,
  DialogScrollHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorCode, post } from "@/lib/api/_client";
import { useClientAccessSettings } from "@/lib/hooks/use-app-bootstrap";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useUpdateClient } from "@/lib/hooks/use-client-detail";
import { useActiveTenantUsers, useMe } from "@/lib/hooks/use-clients";
import { useInvalidateClientAccessData, useMyTeamIds } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { ClientType, ClientWithCustomValues } from "@/lib/types/client";
import { displayUserName } from "@/lib/user-display";
import type { ClientTab, CustomField } from "../settings/settings-types";
import { ClientAccessFields } from "./client-access-fields";
import { ClientAccessReadOnlyHint } from "./client-access-read-only-hint";
import { T } from "./client-dialog.i18n";

type ClientDialogProps = {
  open: boolean;
  client: ClientWithCustomValues | null;
  customFields: CustomField[];
  clientTabs?: ClientTab[];
  onClose: () => void;
};

export function ClientDialog({
  open,
  client,
  customFields,
  clientTabs = [],
  onClose,
}: ClientDialogProps) {
  const { userLanguage, tenantSupportedLanguages, tenantDefaultLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const [formData, setFormData] = useState({
    name: "",
    type: "Person" as ClientType,
    email: "",
    mobilePhone: "",
    ssn: "",
    dateOfBirth: "",
    orgId: "",
    country: "",
    language: "",
    clientCostCategoryId: "",
  });
  const [ownerId, setOwnerId] = useState<string>("");
  const [isPrivate, setIsPrivate] = useState(false);
  // null = untouched: an existing client shows its own teams; a new one prefills with the
  // creator's teams while team access is on (the server applies the same default when none
  // are sent), and starts without teams otherwise.
  const [teamsDraft, setTeamsDraft] = useState<string[] | null>(null);
  const accessSettings = useClientAccessSettings();
  const { me } = useMe();
  const { tenantUsers } = useActiveTenantUsers();
  const prefillMyTeams = accessSettings.teamAccessEnabled && client === null;
  const myTeamIds = useMyTeamIds({ enabled: prefillMyTeams });
  const teamIds =
    teamsDraft ??
    (client ? (client.teams ?? []).map((team) => team.id) : prefillMyTeams ? myTeamIds : []);
  const canManageAccess =
    client === null || client.ownerId === me?.id || (me?.permissions ?? []).includes("admin");
  const [customValues, setCustomValues] = useState<Record<string, string>>({});
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const invalidateClientAccessData = useInvalidateClientAccessData();
  const updateClientMutation = useUpdateClient();

  const { clientCostCategories } = useClientCostCategories();
  const activeCostCategories = clientCostCategories.filter((c) => c.isActive);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        type: client.type || "Person",
        email: client.email || "",
        mobilePhone: client.mobilePhone || "",
        ssn: client.ssn || "",
        dateOfBirth: client.dateOfBirth || "",
        orgId: client.orgId || "",
        country: client.country || "",
        language: client.language || "",
        clientCostCategoryId: client.clientCostCategoryId || "",
      });
      setOwnerId(client.ownerId || "");
      setIsPrivate(client.isPrivate ?? false);

      // Load existing custom field values
      const existingValues: Record<string, string> = {};
      client.customValues.forEach((cv) => {
        existingValues[cv.customFieldId] = cv.value;
      });
      setCustomValues(existingValues);
    } else {
      setFormData({
        name: "",
        type: "Person",
        email: "",
        mobilePhone: "",
        ssn: "",
        dateOfBirth: "",
        orgId: "",
        country: "",
        language: tenantDefaultLanguage,
        clientCostCategoryId: "",
      });
      setOwnerId("");
      setIsPrivate(false);
      setCustomValues({});
    }
    setTeamsDraft(null);
    setError(null);
    // Set initial active tab to "default" when tabs are configured
    if (clientTabs.length > 0) {
      setActiveTab("default");
    } else {
      setActiveTab(null);
    }
  }, [client, open, clientTabs, tenantDefaultLanguage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    startTransition(async () => {
      try {
        const dataToSubmit = {
          ...formData,
          customValues,
          ...(client && { ownerId: ownerId || undefined }),
          ...(accessSettings.privateEnabled ? { isPrivate } : {}),
          // Teams are sent whatever the settings, but only once the picker was touched: on
          // create the server then applies the creator's-teams default (team access on only),
          // and an edit never writes back a stale copy of the client's teams.
          ...(teamsDraft !== null ? { teamIds: teamsDraft } : {}),
        };

        if (client) {
          await updateClientMutation.mutateAsync({ clientId: client.id, data: dataToSubmit });
        } else {
          await post("/internal/clients", dataToSubmit);
          // Clients, plus what a client's teams/owner/privacy feed: team client counts,
          // the dashboard's visibility-filtered widgets and the Admin impact preview.
          invalidateClientAccessData();
        }
        onClose();
      } catch (err: any) {
        setError(
          apiErrorCode(err) === "client_access_manage_denied"
            ? t.accessManageDenied
            : err?.message || t.failedToSave,
        );
      }
    });
  };

  const renderCustomField = (field: CustomField) => {
    const value = customValues[field.id] || "";
    // Use localized field name if available
    const fieldName = localize(field.nameI18n) || field.name;

    switch (field.type) {
      case "text":
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <Input
              id={field.id}
              value={value}
              onChange={(e) => setCustomValues({ ...customValues, [field.id]: e.target.value })}
              placeholder={t.enterField(fieldName.toLowerCase())}
            />
          </div>
        );

      case "textarea":
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <Textarea
              id={field.id}
              value={value}
              onChange={(e) => setCustomValues({ ...customValues, [field.id]: e.target.value })}
              placeholder={t.enterField(fieldName.toLowerCase())}
              rows={3}
            />
          </div>
        );

      case "number":
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <Input
              id={field.id}
              type="number"
              value={value}
              onChange={(e) => setCustomValues({ ...customValues, [field.id]: e.target.value })}
              placeholder={t.enterField(fieldName.toLowerCase())}
            />
          </div>
        );

      case "date":
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <DatePicker
              id={field.id}
              value={value}
              onChange={(next) => setCustomValues({ ...customValues, [field.id]: next })}
            />
          </div>
        );

      case "datetime":
        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <DateTimePicker
              id={field.id}
              value={value}
              onChange={(next) => setCustomValues({ ...customValues, [field.id]: next })}
            />
          </div>
        );

      case "dropdown": {
        // Options carry stable ids (QL-123): the id is stored, the localized
        // label is shown. Archived options stay visible only while selected.
        const options = (field.options ?? []).filter((o) => !o.isArchived || o.id === value);

        return (
          <div key={field.id} className="space-y-2">
            <Label htmlFor={field.id}>{fieldName}</Label>
            <Select
              value={value}
              onValueChange={(val) => setCustomValues({ ...customValues, [field.id]: val })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t.selectField(fieldName.toLowerCase())} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {localize(option.labelI18n) || option.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }

      case "boolean":
        // Stores "true"/"false" explicitly on toggle; untouched stays empty.
        return (
          <OptionRow key={field.id}>
            <Checkbox
              id={field.id}
              checked={value === "true"}
              onCheckedChange={(checked) =>
                setCustomValues({
                  ...customValues,
                  [field.id]: checked === true ? "true" : "false",
                })
              }
            />
            <Label htmlFor={field.id} className="font-normal">
              {fieldName}
            </Label>
          </OptionRow>
        );

      case "radio": {
        const options = (field.options ?? []).filter((o) => !o.isArchived || o.id === value);
        return (
          <div key={field.id} className="space-y-2">
            <Label>{fieldName}</Label>
            <RadioGroup
              value={value}
              onValueChange={(val) => setCustomValues({ ...customValues, [field.id]: val })}
            >
              {options.map((option) => (
                <OptionRow key={option.id}>
                  <RadioGroupItem id={`${field.id}_${option.id}`} value={option.id} />
                  <Label htmlFor={`${field.id}_${option.id}`} className="font-normal">
                    {localize(option.labelI18n) || option.id}
                  </Label>
                </OptionRow>
              ))}
            </RadioGroup>
          </div>
        );
      }

      case "multiselect": {
        const selected = parseMultiselectValue(value);
        const options = (field.options ?? []).filter(
          (o) => !o.isArchived || selected.includes(o.id),
        );
        const toggleOption = (optionId: string, checked: boolean) => {
          const next = checked ? [...selected, optionId] : selected.filter((id) => id !== optionId);
          // Keep stored order stable by option order; store "" when empty.
          const ordered = options.filter((o) => next.includes(o.id)).map((o) => o.id);
          setCustomValues({
            ...customValues,
            [field.id]: ordered.length > 0 ? JSON.stringify(ordered) : "",
          });
        };
        return (
          <div key={field.id} className="space-y-2">
            <Label>{fieldName}</Label>
            <div>
              {options.map((option) => (
                <OptionRow key={option.id}>
                  <Checkbox
                    id={`${field.id}_${option.id}`}
                    checked={selected.includes(option.id)}
                    onCheckedChange={(checked) => toggleOption(option.id, checked === true)}
                  />
                  <Label htmlFor={`${field.id}_${option.id}`} className="font-normal">
                    {localize(option.labelI18n) || option.id}
                  </Label>
                </OptionRow>
              ))}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogScrollContent className="max-w-3xl">
        <DialogScrollHeader>
          <DialogTitle>{client ? t.editClient : t.addNewClient}</DialogTitle>
          <DialogDescription>
            {client ? t.updateDescription : t.createDescription}
          </DialogDescription>
        </DialogScrollHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col min-h-0">
          <DialogBody className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {!client && (
              <div className="space-y-2">
                <Label htmlFor="type">{t.type}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(val) => {
                    const type = val as ClientType;
                    // The date-of-birth field only renders for a Person, but
                    // `handleSubmit` spreads the whole form — so switching to
                    // Company or Group used to submit the date that was no
                    // longer on screen, giving a legal entity a birthday and
                    // logging it as an identity change.
                    setFormData({
                      ...formData,
                      type,
                      ...(type !== "Person" && { dateOfBirth: "" }),
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectType} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Person">{t.person}</SelectItem>
                    <SelectItem value="Company">{t.company}</SelectItem>
                    <SelectItem value="Group">{t.group}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Render fields - with tabs if configured, otherwise flat list */}
            {(() => {
              // Get all fields relevant to this client type
              const allFields = customFields.filter((field) =>
                fieldAppliesToClient(field.clientType, formData.type),
              );

              // Helper to get tabId for a standard field
              const getStandardFieldTabId = (fieldId: string) => {
                const field = customFields.find((f) => f.id === fieldId);
                return field?.tabId || null;
              };

              // Standard field renderers - each checks if it should render in current tab
              const renderNameField = () => (
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t.name} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={
                      formData.type === "Company"
                        ? t.namePlaceholderCompany
                        : formData.type === "Group"
                          ? t.namePlaceholderGroup
                          : t.namePlaceholderPerson
                    }
                    required
                  />
                </div>
              );

              const renderEmailField = () => (
                <div className="space-y-2">
                  <Label htmlFor="email">{t.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t.emailPlaceholder}
                  />
                </div>
              );

              const renderMobilePhoneField = () => (
                <div className="space-y-2">
                  <Label htmlFor="mobilePhone">{t.mobilePhone}</Label>
                  <Input
                    id="mobilePhone"
                    type="tel"
                    value={formData.mobilePhone}
                    onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
                    placeholder={t.mobilePhonePlaceholder}
                  />
                </div>
              );

              const renderSsnField = () =>
                formData.type === "Person" ? (
                  <div className="space-y-2">
                    <Label htmlFor="ssn">{t.ssn}</Label>
                    <Input
                      id="ssn"
                      type="text"
                      value={formData.ssn}
                      onChange={(e) => setFormData({ ...formData, ssn: e.target.value })}
                      placeholder={t.ssnPlaceholder}
                    />
                  </div>
                ) : null;

              const renderDateOfBirthField = () =>
                formData.type === "Person" ? (
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">{t.dateOfBirth}</Label>
                    <DatePicker
                      id="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={(value) => setFormData({ ...formData, dateOfBirth: value })}
                    />
                  </div>
                ) : null;

              const renderOrgIdField = () =>
                formData.type === "Company" ? (
                  <div className="space-y-2">
                    <Label htmlFor="orgId">{t.orgId}</Label>
                    <Input
                      id="orgId"
                      type="text"
                      value={formData.orgId}
                      onChange={(e) => setFormData({ ...formData, orgId: e.target.value })}
                      placeholder={t.orgIdPlaceholder}
                    />
                  </div>
                ) : null;

              const renderCountryField = () => (
                <div className="space-y-2">
                  <Label htmlFor="country">{t.country}</Label>
                  <CountrySelect
                    id="country"
                    className="flex-1"
                    value={formData.country}
                    onValueChange={(val) => setFormData({ ...formData, country: val })}
                    placeholder={t.selectCountry}
                    onClear={() => setFormData({ ...formData, country: "" })}
                    clearLabel={t.clear}
                  />
                </div>
              );

              const renderLanguageField = () => (
                <div className="space-y-2">
                  <Label htmlFor="language">{t.language}</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.language || undefined}
                      onValueChange={(val) => setFormData({ ...formData, language: val })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder={t.selectLanguage} />
                      </SelectTrigger>
                      <SelectContent>
                        {tenantSupportedLanguages.map((lang) => (
                          <SelectItem key={lang} value={lang}>
                            {LOCALE_NAMES[lang as LocaleCode] || lang}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.language && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, language: "" })}
                      >
                        {t.clear}
                      </Button>
                    )}
                  </div>
                </div>
              );

              const renderCostCategoryField = () =>
                activeCostCategories.length > 0 ? (
                  <div className="space-y-2">
                    <Label htmlFor="clientCostCategory">{t.costCategory}</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.clientCostCategoryId || undefined}
                        onValueChange={(val) =>
                          setFormData({ ...formData, clientCostCategoryId: val })
                        }
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder={t.selectCostCategory} />
                        </SelectTrigger>
                        <SelectContent>
                          {activeCostCategories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {localize(cat.nameI18n)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.clientCostCategoryId && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({ ...formData, clientCostCategoryId: "" })}
                        >
                          {t.clear}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : null;

              // Active users only; a deleted current owner stays selectable (marked) so the
              // field does not silently change to someone else.
              const ownerOptions =
                client?.owner && !tenantUsers.some((u) => u.id === client.owner?.id)
                  ? [
                      {
                        id: client.owner.id,
                        name: displayUserName(client.owner, userLanguage) ?? client.owner.id,
                      },
                      ...tenantUsers,
                    ]
                  : tenantUsers;

              const renderAccessFields = () => (
                <ClientAccessFields
                  settings={accessSettings}
                  canManage={canManageAccess}
                  isPrivate={isPrivate}
                  teamIds={teamIds}
                  onPrivateChange={setIsPrivate}
                  onTeamsChange={setTeamsDraft}
                />
              );

              const renderOwnerField = () =>
                client && ownerOptions.length > 0 ? (
                  <div className="space-y-2">
                    <Label htmlFor="owner">{t.owner}</Label>
                    <SearchableSelect
                      id="owner"
                      value={ownerId}
                      onValueChange={setOwnerId}
                      options={ownerOptions.map((user) => ({
                        value: user.id,
                        label: user.name || t.unnamedUser,
                      }))}
                      placeholder={t.selectOwner}
                      searchPlaceholder={t.searchOwner}
                      noResultsMessage={t.noOwnerResults}
                      disabled={!canManageAccess}
                    />
                    {!canManageAccess && <ClientAccessReadOnlyHint />}
                  </div>
                ) : null;

              // Map of standard field IDs to their render functions
              const standardFieldRenderers: Record<string, () => React.ReactNode> = {
                "standard-name": renderNameField,
                "standard-email": renderEmailField,
                "standard-mobilePhone": renderMobilePhoneField,
                "standard-ssn": renderSsnField,
                "standard-dateOfBirth": renderDateOfBirthField,
                "standard-orgId": renderOrgIdField,
                "standard-country": renderCountryField,
                "standard-language": renderLanguageField,
                // The teams picker always shows, so this slot is never empty.
                "standard-owner": () => (
                  <div className="space-y-4">
                    {renderOwnerField()}
                    {renderAccessFields()}
                  </div>
                ),
                "standard-costCategory": renderCostCategoryField,
              };

              // Render fields for a specific tab (null = default tab)
              const renderFieldsForTab = (tabId: string | null) => {
                const elements: React.ReactNode[] = [];

                // Render standard fields assigned to this tab
                Object.entries(standardFieldRenderers).forEach(([fieldId, renderer]) => {
                  if (getStandardFieldTabId(fieldId) === tabId) {
                    const element = renderer();
                    if (element) elements.push(<div key={fieldId}>{element}</div>);
                  }
                });

                // Render KYC field if assigned to this tab (only when editing)

                // Render custom fields assigned to this tab
                allFields
                  .filter((f) => !f.isStandard && f.tabId === tabId)
                  .forEach((field) => {
                    elements.push(renderCustomField(field));
                  });

                return elements;
              };

              // If no tabs configured, render all fields flat
              if (clientTabs.length === 0) {
                return (
                  <>
                    {renderNameField()}
                    {renderEmailField()}
                    {renderMobilePhoneField()}
                    {renderSsnField()}
                    {renderDateOfBirthField()}
                    {renderOrgIdField()}
                    {renderCountryField()}
                    {renderLanguageField()}
                    {renderCostCategoryField()}
                    {renderOwnerField()}
                    {renderAccessFields()}
                    {allFields
                      .filter((f) => !f.isStandard)
                      .map((field) => renderCustomField(field))}
                  </>
                );
              }

              // With tabs: show Default tab + configured tabs
              // Using grid to stack all tabs - container takes height of tallest
              return (
                <Tabs value={activeTab || "default"} onValueChange={setActiveTab}>
                  <TabsList className="w-full">
                    <TabsTrigger value="default" className="flex-1">
                      {t.default}
                    </TabsTrigger>
                    {clientTabs.map((tab) => (
                      <TabsTrigger key={tab.id} value={tab.id} className="flex-1">
                        {localize(tab.nameI18n)}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="grid mt-4">
                    <TabsContent
                      value="default"
                      forceMount
                      className="space-y-4 col-start-1 row-start-1 data-[state=inactive]:invisible"
                    >
                      {renderFieldsForTab(null).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {t.noFieldsInTab}
                        </p>
                      ) : (
                        renderFieldsForTab(null)
                      )}
                    </TabsContent>
                    {clientTabs.map((tab) => (
                      <TabsContent
                        key={tab.id}
                        value={tab.id}
                        forceMount
                        className="space-y-4 col-start-1 row-start-1 data-[state=inactive]:invisible"
                      >
                        {renderFieldsForTab(tab.id).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            {t.noFieldsInTab}
                          </p>
                        ) : (
                          renderFieldsForTab(tab.id)
                        )}
                      </TabsContent>
                    ))}
                  </div>
                </Tabs>
              );
            })()}
          </DialogBody>

          <DialogScrollFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {client ? t.save : t.addClient}
            </Button>
          </DialogScrollFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  );
}
